<template>
    <div v-if="loading" class="w-96 flex justify-center items-center h-96 bg-detail-bg relative">
        <Spinner :text="$t('common:text-loading')" />
    </div>
    <ReferenceUnavailable v-else-if="!variant?.id" class="p-6 lg:p-14 xl:p-16 bg-light rounded-panel" />
    <article v-else class="bg-detail-bg w-full max-w-2xl relative z-[51] pt-3 sm:pt-10">
        <Scrollbar class="w-full max-h-dmv mt-5" :options="{ scrollbars: { autoHide: 'never' } }">
            <ReferenceDetailInfo :variant="variant" size="modal" @select:tab:comments="selectTabComments" />
            <div class="w-full flex items-center justify-center py-8">
              <Button size="big" :on-click="closeModal">
                <span class="text-sm px-9">{{ $t('text-close') }}</span>
              </Button>
            </div>
        </Scrollbar>
    </article>
</template>

<script setup>
import _ from 'lodash-es';
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { useAuthStore } from '@stores/auth';
import { useSelectedStore } from '@stores/selected';
import Spinner from "@components/ui/loaders/spinner/spinner";
import Button from "@components/ui/button";
import ReferenceUnavailable from "@components/reference/reference-details/reference-unavailable";
import ReferenceDetailInfo from "@components/reference/reference-details/reference-detail-info";
import Scrollbar from "@components/ui/scrollbar";

const loading = ref(true);
const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const auth = useAuthStore();
const store = useSelectedStore();
const routeLang = useRouteLang();

const variant = ref({});
const setVariant = (data) => {
    _.assignIn(variant.value, data);
}

const reload = async () => {
    loading.value = true;
    const { data, error, status } = await useApiFetch(API_ENDPOINTS.CONTENT_POST_ID, {
      method: 'GET',
      path: {
        id: store.content.id
      },
      params: {
        locale: routeLang.locale.value
      }
    });
    if (status.value === 'error') {
      if (import.meta.client && !auth.reloadTimeout) {
        $toast.error(t('error.reference-detail.load'));
      }
      sentryCaptureEvent({
        message: 'reference-details-modal-view.reload',
        level: 'error',
        extra: { error: error.value }
      });
    } else {
      setVariant(data.value);
    }
    loading.value = false;
}

onServerPrefetch(async () => await reload());

const contentUpdate = (value) => {
  if (value.id === variant.value.id) {
    variant.value = { ...variant.value, ...value };
  }
};

onMounted(async () => {
  if (import.meta.server || navigator?.onLine) {
    await reload();
  }
  $eventBus.on('content:update', contentUpdate);
  if (store.action === 'SELECT_TAB_COMMENTS') {
    $eventBus.emit('focus:input', 'comment');
  }
});

onUnmounted(() => {
  $eventBus.off('content:update', contentUpdate);
});

const closeModal = () => {
    $eventBus.emit('modal:close');
}

const selectTabComments = (id) => {
    if (id === variant.value.id) {
        $eventBus.emit('focus:input', 'comment');
    }
}

</script>
